// Expandable version history for one ordered option file.
// Replace always creates the next version. A row action restores an older one.

import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Modal, ModalFooter, useConfirm } from '@tkpod/ui';
import { ApiClientError, useApiClient } from '@tkpod/api-client';
import { canonicalDeclaredMime } from '@tkpod/shared';

interface VersionRow {
  versionNo: number;
  operation: string | null;
  fileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  widthPx: number | null;
  heightPx: number | null;
  createdAt: string;
  createdByName: string;
  current: boolean;
  originalUrl: string;
  previewUrl: string | null;
  thumbUrl: string | null;
}

interface VersionList {
  currentVersionNo: number;
  pendingVersionNo: number | null;
  artworkUpdatedAfterClaim: boolean;
  canEdit: boolean;
  editDeniedMessage: string | null;
  versions: VersionRow[];
}

interface PartPlan {
  versionNo: number;
  partCount: number;
  partSizeBytes: number;
}

const OPERATION_LABEL: Record<string, string> = {
  upload: 'Uploaded',
  replace: 'Replaced',
  revert: 'Restored',
};

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function VersionThumb({ version }: { version: VersionRow }) {
  if (!version.thumbUrl) {
    return (
      <span className="inline-flex h-10 w-10 items-center justify-center rounded border border-border bg-surface-secondary text-[10px] text-text-tertiary">
        File
      </span>
    );
  }
  return (
    <a href={version.previewUrl ?? version.thumbUrl} target="_blank" rel="noopener noreferrer">
      <img
        src={version.thumbUrl}
        alt={version.fileName ? `Preview of ${version.fileName}` : 'File preview'}
        className="h-10 w-10 rounded border border-border bg-surface-secondary object-cover"
      />
    </a>
  );
}

function formatBytes(bytes: number | null): string | null {
  if (bytes == null || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function OptionUploadVersionActions({
  token,
  basePath,
  orderQueryKey,
  fileLabel,
  linkClass = 'text-accent',
}: {
  token: string;
  basePath: string;
  orderQueryKey: readonly unknown[];
  fileLabel?: string | null;
  linkClass?: string;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const fileRef = useRef<HTMLInputElement>(null);
  const confirmingRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const versionsQuery = useQuery({
    queryKey: ['option-upload-versions', basePath, token],
    queryFn: () => api.get<VersionList>(`${basePath}/versions`),
    enabled: open,
    refetchInterval: (query) => (query.state.data?.pendingVersionNo ? 3000 : false),
  });

  async function refresh() {
    await versionsQuery.refetch();
    await queryClient.invalidateQueries({ queryKey: orderQueryKey });
  }

  async function replace(file: File) {
    setBusy(true);
    setError(null);
    try {
      const mimeType = canonicalDeclaredMime(file.type, file.name) || 'application/octet-stream';
      const plan = await api.post<PartPlan>(`${basePath}/replace`, {
        fileName: file.name,
        mimeType,
        sizeBytes: file.size,
      });
      const partNumbers = Array.from({ length: plan.partCount }, (_, index) => index + 1);
      const signed = await api.post<{ parts: Array<{ partNumber: number; url: string }> }>(
        `${basePath}/replace/parts`,
        { partNumbers },
      );
      const parts: Array<{ partNumber: number; etag: string }> = [];
      for (const part of signed.parts) {
        const start = (part.partNumber - 1) * plan.partSizeBytes;
        const slice = file.slice(start, Math.min(file.size, start + plan.partSizeBytes));
        // A sliced File has an empty type, so the browser omits Content-Type
        // and the local API rejects the part with 415.
        const response = await fetch(part.url, {
          method: 'PUT',
          body: slice,
          headers: { 'Content-Type': mimeType },
        });
        if (!response.ok) throw new Error('Upload failed');
        const header = response.headers.get('etag');
        const json = (await response.clone().json().catch(() => null)) as { etag?: unknown } | null;
        const etag = header?.trim() || (typeof json?.etag === 'string' ? json.etag.trim() : '');
        if (!etag) throw new Error('The upload part did not return an ETag.');
        parts.push({ partNumber: part.partNumber, etag });
      }
      await api.post(`${basePath}/replace/complete`, { parts });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : err instanceof Error ? err.message : 'Replace failed');
    } finally {
      setBusy(false);
    }
  }

  async function revert(version: VersionRow) {
    confirmingRef.current = true;
    const ok = await confirm({
      title: `Use version ${version.versionNo}?`,
      description: 'The order will serve this file again. Newer versions stay in the history.',
      confirmLabel: 'Use this version',
    });
    confirmingRef.current = false;
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`${basePath}/revert`, { versionNo: version.versionNo });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Revert failed');
    } finally {
      setBusy(false);
    }
  }

  const data = versionsQuery.data;
  const locked = data != null && !data.canEdit;
  const actionsDisabled = busy || locked || data?.pendingVersionNo != null;

  function closeHistory() {
    if (!confirmingRef.current) setOpen(false);
  }

  function versionMeta(version: VersionRow): string {
    return [
      version.mimeType,
      formatBytes(version.sizeBytes),
      version.widthPx != null && version.heightPx != null
        ? `${version.widthPx}×${version.heightPx}`
        : null,
    ].filter(Boolean).join(' · ');
  }

  function versionLinks(version: VersionRow) {
    return (
      <>
        <a
          href={version.originalUrl}
          className={`font-medium hover:underline ${linkClass}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Original
        </a>
        {version.previewUrl && (
          <a
            href={version.previewUrl}
            className={`font-medium hover:underline ${linkClass}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Preview
          </a>
        )}
      </>
    );
  }

  function versionAction(version: VersionRow) {
    if (version.current) {
      return (
        <button
          type="button"
          className={`font-medium hover:underline disabled:opacity-50 ${linkClass}`}
          disabled={actionsDisabled}
          onClick={() => fileRef.current?.click()}
        >
          {busy ? 'Working…' : 'Replace file'}
        </button>
      );
    }
    return (
      <button
        type="button"
        className={`font-medium hover:underline disabled:opacity-50 ${linkClass}`}
        disabled={actionsDisabled}
        onClick={() => void revert(version)}
      >
        Use this version
      </button>
    );
  }

  return (
    <div className="mt-1">
      {dialog}
      <button
        type="button"
        className={`text-xs font-medium hover:underline ${linkClass}`}
        onClick={() => setOpen(true)}
      >
        Version history
      </button>
      <Modal
        open={open}
        onClose={closeHistory}
        size="xl"
        className="min-w-0"
        title={fileLabel ? `Version history — ${fileLabel}` : 'Version history'}
        description="Replace creates the next version. Use this version makes an older file current again."
      >
        <div className="space-y-3">
          {data?.artworkUpdatedAfterClaim && (
            <p className="text-xs text-text-secondary">
              This file changed after the provider received the order. Resubmit to provider.
            </p>
          )}
          {data?.pendingVersionNo != null && (
            <p className="text-xs text-text-tertiary">
              Version {data.pendingVersionNo} is processing. The current file stays available until it is ready.
            </p>
          )}
          {locked && data.editDeniedMessage && (
            <p className="text-xs text-text-tertiary">{data.editDeniedMessage}</p>
          )}
          {versionsQuery.isLoading && (
            <p className="text-xs text-text-tertiary">Loading version history…</p>
          )}
          {versionsQuery.isError && (
            <p className="text-xs text-red-600">Version history could not be loaded.</p>
          )}
          {data && data.versions.length > 0 && (
            <>
              <ul className="space-y-2 sm:hidden">
                {data.versions.map((version) => {
                  const meta = versionMeta(version);
                  return (
                    <li key={version.versionNo} className="rounded-md border border-border p-3 text-sm">
                      <div className="flex items-start gap-3">
                        <VersionThumb version={version} />
                        <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="font-medium text-text-primary">v{version.versionNo}</span>
                            {version.current && <Badge variant="success" size="sm">Current</Badge>}
                          </div>
                          <p className="text-xs text-text-tertiary">
                            {OPERATION_LABEL[version.operation ?? ''] ?? 'Saved'}
                          </p>
                        </div>
                        <p className="shrink-0 text-right text-xs text-text-secondary">{formatWhen(version.createdAt)}</p>
                      </div>
                      <p className="mt-2 text-text-secondary">{version.createdByName}</p>
                      <p className="truncate text-text-primary" title={version.fileName ?? undefined}>
                        {version.fileName ?? '—'}
                      </p>
                      {meta && <p className="text-xs text-text-tertiary">{meta}</p>}
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                        {versionLinks(version)}
                        {versionAction(version)}
                      </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="hidden max-w-full overflow-x-auto rounded-md border border-border sm:block">
                <table className="w-full text-left text-sm">
                  <thead className="bg-surface-secondary text-text-tertiary">
                    <tr>
                      <th className="px-3 py-2 font-medium">Preview</th>
                      <th className="px-3 py-2 font-medium">Version</th>
                      <th className="px-3 py-2 font-medium">Updated</th>
                      <th className="px-3 py-2 font-medium">Updated by</th>
                      <th className="px-3 py-2 font-medium">File</th>
                      <th className="px-3 py-2 font-medium">Links</th>
                      <th className="px-3 py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.versions.map((version) => {
                      const meta = versionMeta(version);
                      return (
                        <tr key={version.versionNo} className="text-text-secondary">
                          <td className="px-3 py-2 align-middle">
                            <VersionThumb version={version} />
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-text-primary">v{version.versionNo}</span>
                              {version.current && <Badge variant="success" size="sm">Current</Badge>}
                            </div>
                            <p className="text-xs text-text-tertiary">
                              {OPERATION_LABEL[version.operation ?? ''] ?? 'Saved'}
                            </p>
                          </td>
                          <td className="px-3 py-2 align-top whitespace-nowrap">{formatWhen(version.createdAt)}</td>
                          <td className="px-3 py-2 align-top">{version.createdByName}</td>
                          <td className="px-3 py-2 align-top">
                            <p className="max-w-[220px] truncate text-text-primary" title={version.fileName ?? undefined}>
                              {version.fileName ?? '—'}
                            </p>
                            {meta && <p className="text-xs text-text-tertiary">{meta}</p>}
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="flex flex-wrap gap-x-3 gap-y-1">{versionLinks(version)}</div>
                          </td>
                          <td className="px-3 py-2 align-top whitespace-nowrap">{versionAction(version)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            disabled={busy || locked}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void replace(file);
            }}
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <ModalFooter>
            <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={closeHistory}>
              Close
            </Button>
          </ModalFooter>
        </div>
      </Modal>
    </div>
  );
}
